import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { stockAuditAPI, productAPI, warehouseAPI } from '../utils/api';
import { formatDateDMY } from '../utils/dateFormat';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import ProductModal from '../components/ProductModal';


const UNITS = ['Pcs', 'Box', 'Kg', 'Gm', 'Mtr', 'Ltr', 'Set', 'Pack', 'Pair', 'Roll'];
const LOCATIONS = ['Store / Shop', 'Godown / Warehouse', 'Floor Display', 'Backroom'];

const fmtNum = (val, maxDec = 2) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n.toLocaleString('en-IN', { maximumFractionDigits: maxDec });
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return formatDateDMY(isoStr);
    const dateStr = formatDateDMY(isoStr);
    const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${dateStr} at ${timeStr}`;
  } catch {
    return formatDateDMY(isoStr);
  }
};

/**
 * Sorts products strictly by System Stock:
 * 1. Positive stock (> 0) sorted descending
 * 2. Negative stock (< 0) sorted ascending (most negative first)
 * 3. Zero stock (== 0) sorted alphabetically by name
 */
const sortProductsByStockOrder = (items) => {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const stockA = Number(a.current_stock ?? 0);
    const stockB = Number(b.current_stock ?? 0);

    const getGroup = (s) => {
      if (s > 0) return 1; // Positive stock first
      if (s < 0) return 2; // Negative stock second
      return 3;            // Zero stock last
    };

    const groupA = getGroup(stockA);
    const groupB = getGroup(stockB);

    if (groupA !== groupB) {
      return groupA - groupB;
    }

    // Inside Positive group: higher stock on top
    if (groupA === 1) {
      if (stockB !== stockA) return stockB - stockA;
      return (a.name || '').localeCompare(b.name || '');
    }

    // Inside Negative group: most negative first (e.g. -10 before -1)
    if (groupA === 2) {
      if (stockA !== stockB) return stockA - stockB;
      return (a.name || '').localeCompare(b.name || '');
    }

    // Inside Zero group: alphabetical by name
    return (a.name || '').localeCompare(b.name || '');
  });
};



/* ── Stock Audit Page Component ── */
const StockAudit = () => {
  const { darkMode, color } = useTheme();
  const { user, loginRole } = useAuth();
  const { refreshProducts } = useData();
  const acc = color.swatch;

  // Responsive screen detection for PWA & mobile devices
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tabs: 'new' (conduct audit) vs 'history' (view past reports)
  const [activeTab, setActiveTab] = useState('new');

  // Audit setup metadata
  const [auditLocation, setAuditLocation] = useState('Store / Shop');
  const [auditTitle, setAuditTitle] = useState('');
  const [auditNotes, setAuditNotes] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  // Products search & selection state
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductDD, setShowProductDD] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dropdownStockFilter, setDropdownStockFilter] = useState('all'); // 'all', 'positive', 'negative', 'zero'

  // Quick add product modal
  const [showAddProdModal, setShowAddProdModal] = useState(false);

  // Item entry form state
  const [itemPhysicalStock, setItemPhysicalStock] = useState('');
  const [itemLocation, setItemLocation] = useState('');
  const [itemNotes, setItemNotes] = useState('');

  // Table of audited items in this active session
  const [auditedItems, setAuditedItems] = useState([]);

  // Saving / Finishing state
  const [finishing, setFinishing] = useState(false);
  const [showConfirmFinishModal, setShowConfirmFinishModal] = useState(false);

  // Past Audits List state
  const [pastAudits, setPastAudits] = useState([]);
  const [loadingPastAudits, setLoadingPastAudits] = useState(false);
  const [pastFilterStatus, setPastFilterStatus] = useState('');
  const [pastSearch, setPastSearch] = useState('');
  const [pastStats, setPastStats] = useState({});

  // View / Reconcile Single Audit Report Modal
  const [viewAuditReport, setViewAuditReport] = useState(null);
  const [viewAuditItems, setViewAuditItems] = useState([]);
  const [loadingReportDetails, setLoadingReportDetails] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileSuccessMsg, setReconcileSuccessMsg] = useState('');

  const prodInputRef = useRef(null);
  const physStockInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const cardBg = darkMode ? '#1E293B' : '#FFFFFF';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const pg = useMemo(() => ({ color: darkMode ? '#F1F5F9' : '#0F172A' }), [darkMode]);
  const muted = useMemo(() => ({ color: darkMode ? '#64748B' : '#94A3B8' }), [darkMode]);
  const inp = {
    width: '100%',
    padding: isMobile ? '11px 12px' : '9px 12px',
    borderRadius: 8,
    border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
    background: darkMode ? '#0F172A' : '#F8FAFC',
    color: pg.color,
    fontSize: isMobile ? 14 : 13,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl = { fontSize: 11, fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' };

  // Load initial product catalog (up to 300 products for fast offline/instant search) and warehouses
  useEffect(() => {
    (async () => {
      try {
        const [prodRes, whRes] = await Promise.all([
          productAPI.list({ limit: 300 }),
          warehouseAPI.list().catch(() => ({ data: [] }))
        ]);
        const list = prodRes.data?.products || [];
        setAllProducts(list);
        if (Array.isArray(whRes.data)) setWarehouses(whRes.data);
      } catch (err) {
        console.error('Failed to load initial products/warehouses', err);
      }
    })();
  }, []);

  // Sync itemLocation with auditLocation by default
  useEffect(() => {
    setItemLocation(auditLocation);
  }, [auditLocation]);

  // Filter and sort products according to:
  // 1. Positive stock (> 0)
  // 2. Negative stock (< 0)
  // 3. Zero stock (== 0)
  const computeSortedDropdownProducts = useCallback((query = '', filter = 'all') => {
    let source = allProducts;
    const q = query.trim().toLowerCase();

    if (q) {
      source = allProducts.filter(p =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.barcode && p.barcode.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q))
      );
    }

    // Apply quick stock filter if active
    if (filter === 'positive') {
      source = source.filter(p => Number(p.current_stock || 0) > 0);
    } else if (filter === 'negative') {
      source = source.filter(p => Number(p.current_stock || 0) < 0);
    } else if (filter === 'zero') {
      source = source.filter(p => Number(p.current_stock || 0) === 0);
    }

    // Strictly sort: Positive > Negative > Zero
    return sortProductsByStockOrder(source);
  }, [allProducts]);

  // Product search autocomplete with strictly sorted priority: Positive > Negative > Zero
  useEffect(() => {
    const sorted = computeSortedDropdownProducts(productQuery, dropdownStockFilter);
    setProductResults(sorted);

    // If user has query or dropdown is open, keep results refreshed
    if (productQuery.trim().length >= 1) {
      const timer = setTimeout(async () => {
        try {
          const res = await productAPI.list({ search: productQuery.trim(), limit: 40 });
          const serverList = res.data?.products || [];
          if (serverList.length > 0) {
            let combined = [...serverList];
            if (dropdownStockFilter === 'positive') combined = combined.filter(p => Number(p.current_stock || 0) > 0);
            else if (dropdownStockFilter === 'negative') combined = combined.filter(p => Number(p.current_stock || 0) < 0);
            else if (dropdownStockFilter === 'zero') combined = combined.filter(p => Number(p.current_stock || 0) === 0);
            setProductResults(sortProductsByStockOrder(combined));
          }
        } catch {
          // fallback to local computed
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [productQuery, dropdownStockFilter, computeSortedDropdownProducts]);

  // Close product dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProductDD(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // When user clicks/focuses product input, show dropdown with positive -> negative -> zero stock
  const handleFocusProductSearch = () => {
    const sorted = computeSortedDropdownProducts(productQuery, dropdownStockFilter);
    setProductResults(sorted);
    setShowProductDD(true);
  };

  // When a product is selected from dropdown or quick add
  const handleSelectProduct = useCallback((product) => {
    setSelectedProduct(product);
    setProductQuery(product.name);
    setShowProductDD(false);
    setItemPhysicalStock(String(product.current_stock ?? ''));
    setItemLocation(auditLocation);
    setItemNotes('');
    setTimeout(() => {
      if (physStockInputRef.current) {
        physStockInputRef.current.focus();
        physStockInputRef.current.select();
      }
    }, 60);
  }, [auditLocation]);

  // Product added callback from detailed ProductModal
  const handleProductCreated = async (newProd) => {
    setShowAddProdModal(false);
    // 1. Refresh global application products cache so Billing, Products page, etc. immediately see it
    if (typeof refreshProducts === 'function') {
      try {
        await refreshProducts(true);
      } catch (e) {
        console.error('Failed to refresh global products', e);
      }
    }

    // 2. Refresh local StockAudit product list to guarantee fresh stock numbers
    let updatedList = [];
    try {
      const res = await productAPI.list({ limit: 1000 });
      const prods = res.data?.products || res.data || [];
      if (Array.isArray(prods) && prods.length > 0) {
        setAllProducts(prods);
        updatedList = prods;
      }
    } catch {
      if (newProd) {
        setAllProducts(prev => [newProd, ...prev.filter(p => p.id !== newProd.id)]);
      }
    }

    // 3. Immediately select the newly created product for the audit
    if (newProd) {
      const matched = updatedList.find(p => p.id === newProd.id) || newProd;
      handleSelectProduct(matched);
    }
  };


  // Add confirmed item to active audit list
  const handleConfirmItem = (e) => {
    if (e) e.preventDefault();
    if (!selectedProduct) {
      alert('Please select a product from dropdown or add a new one first');
      return;
    }
    if (itemPhysicalStock === '' || isNaN(parseFloat(itemPhysicalStock))) {
      alert('Please enter a valid physical stock quantity');
      return;
    }

    const sysStock = parseFloat(selectedProduct.current_stock || 0);
    const physStock = parseFloat(itemPhysicalStock);
    const variance = +(physStock - sysStock).toFixed(2);
    const pPrice = parseFloat(selectedProduct.purchase_price || selectedProduct.selling_price || 0);
    const varValue = +(variance * pPrice).toFixed(2);

    // Check if already in table
    const existingIndex = auditedItems.findIndex(i => i.product_id === selectedProduct.id);
    if (existingIndex >= 0) {
      const confirmReplace = window.confirm(
        `"${selectedProduct.name}" is already in this audit (Qty: ${auditedItems[existingIndex].physical_stock}). Update its physical count to ${physStock}?`
      );
      if (!confirmReplace) return;

      setAuditedItems(prev => {
        const copy = [...prev];
        copy[existingIndex] = {
          ...copy[existingIndex],
          physical_stock: physStock,
          variance,
          variance_value: varValue,
          location: itemLocation || auditLocation,
          notes: itemNotes.trim(),
        };
        return copy;
      });
    } else {
      const newItem = {
        key: Date.now() + Math.random(),
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        sku: selectedProduct.sku || '',
        category: selectedProduct.category || '',
        unit: selectedProduct.unit || 'Pcs',
        location: itemLocation || auditLocation,
        system_stock: sysStock,
        physical_stock: physStock,
        variance,
        purchase_price: pPrice,
        selling_price: parseFloat(selectedProduct.selling_price || 0),
        variance_value: varValue,
        notes: itemNotes.trim(),
      };
      setAuditedItems(prev => [newItem, ...prev]);
    }

    // Reset input row for next item
    setSelectedProduct(null);
    setProductQuery('');
    setItemPhysicalStock('');
    setItemNotes('');
    if (prodInputRef.current) prodInputRef.current.focus();
  };

  // Remove an item from active audit table
  const handleRemoveItem = (key) => {
    setAuditedItems(prev => prev.filter(i => i.key !== key));
  };

  // Inline edit counted physical stock
  const handleUpdateItemQty = (key, newQty) => {
    const parsed = parseFloat(newQty);
    if (isNaN(parsed)) return;
    setAuditedItems(prev => prev.map(item => {
      if (item.key !== key) return item;
      const variance = +(parsed - item.system_stock).toFixed(2);
      const variance_value = +(variance * item.purchase_price).toFixed(2);
      return { ...item, physical_stock: parsed, variance, variance_value };
    }));
  };

  // Live Summary calculation for the active audit
  const activeMetrics = useMemo(() => {
    const totalItems = auditedItems.length;
    let totalSys = 0;
    let totalPhys = 0;
    let matchedCount = 0;
    let shortageCount = 0;
    let surplusCount = 0;
    let totalShortageUnits = 0;
    let totalSurplusUnits = 0;
    let netVarianceValue = 0;

    auditedItems.forEach(item => {
      totalSys += item.system_stock;
      totalPhys += item.physical_stock;
      netVarianceValue += item.variance_value;
      if (item.variance === 0) {
        matchedCount++;
      } else if (item.variance < 0) {
        shortageCount++;
        totalShortageUnits += Math.abs(item.variance);
      } else {
        surplusCount++;
        totalSurplusUnits += item.variance;
      }
    });

    return {
      totalItems,
      totalSys,
      totalPhys,
      matchedCount,
      shortageCount,
      surplusCount,
      totalShortageUnits,
      totalSurplusUnits,
      netVariance: +(totalPhys - totalSys).toFixed(2),
      netVarianceValue: +netVarianceValue.toFixed(2),
    };
  }, [auditedItems]);

  // Finish Audit & Save to Database
  const handleSaveAudit = async () => {
    if (auditedItems.length === 0) {
      alert('Please audit at least 1 item before finishing');
      return;
    }

    setFinishing(true);
    try {
      const payload = {
        title: auditTitle.trim() || `Physical Stock Audit (${auditLocation})`,
        location: auditLocation,
        warehouse_id: selectedWarehouseId || null,
        status: 'completed',
        notes: auditNotes.trim() || null,
        items: auditedItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku,
          category: item.category,
          unit: item.unit,
          location: item.location,
          system_stock: item.system_stock,
          physical_stock: item.physical_stock,
          purchase_price: item.purchase_price,
          selling_price: item.selling_price,
          notes: item.notes,
        })),
      };

      const res = await stockAuditAPI.create(payload);
      setShowConfirmFinishModal(false);
      setAuditedItems([]);
      setProductQuery('');
      setSelectedProduct(null);
      setAuditNotes('');

      // Open the saved audit report directly
      await handleOpenAuditDetails(res.data.id);
      setActiveTab('history');
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Failed to save stock audit');
    } finally {
      setFinishing(false);
    }
  };

  // Load past audits
  const loadPastAudits = useCallback(async () => {
    setLoadingPastAudits(true);
    try {
      const res = await stockAuditAPI.list({
        status: pastFilterStatus || undefined,
        search: pastSearch.trim() || undefined,
        limit: 50,
      });
      setPastAudits(res.data?.items || []);
      setPastStats(res.data?.stats || {});
    } catch (err) {
      console.error('Failed to load past audits', err);
    } finally {
      setLoadingPastAudits(false);
    }
  }, [pastFilterStatus, pastSearch]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadPastAudits();
    }
  }, [activeTab, loadPastAudits]);

  // Open single audit report details
  const handleOpenAuditDetails = async (auditId) => {
    setLoadingReportDetails(true);
    setReconcileSuccessMsg('');
    try {
      const res = await stockAuditAPI.get(auditId);
      setViewAuditReport(res.data.audit);
      setViewAuditItems(res.data.items || []);
    } catch (err) {
      alert('Failed to load audit report details: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingReportDetails(false);
    }
  };

  // Reconcile Audit into Main Inventory
  const handleReconcile = async (auditId) => {
    if (!window.confirm(
      'Are you sure you want to reconcile this audit? This will update main stock (current_stock) in your inventory to match the physical audit count, and log entries in stock movements.'
    )) {
      return;
    }

    setReconciling(true);
    setReconcileSuccessMsg('');
    try {
      const res = await stockAuditAPI.reconcile(auditId);
      setReconcileSuccessMsg(res.data?.message || 'Stock successfully reconciled into main inventory!');
      const updated = await stockAuditAPI.get(auditId);
      setViewAuditReport(updated.data.audit);
      setViewAuditItems(updated.data.items || []);
      loadPastAudits();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Failed to reconcile stock');
    } finally {
      setReconciling(false);
    }
  };

  // Delete Audit
  const handleDeleteAudit = async (auditId) => {
    if (!window.confirm('Delete this stock audit report? This cannot be undone.')) return;
    try {
      await stockAuditAPI.delete(auditId);
      if (viewAuditReport?.id === auditId) setViewAuditReport(null);
      loadPastAudits();
    } catch (err) {
      alert('Failed to delete audit: ' + (err.response?.data?.detail || err.message));
    }
  };

  const auditorDisplayName = user?.name || user?.owner_name || (loginRole === 'owner' ? 'Shop Owner' : 'Staff Member');

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', paddingBottom: isMobile ? 80 : 40, paddingLeft: isMobile ? 4 : 0, paddingRight: isMobile ? 4 : 0 }}>

        {/* ── Page Header with Top Right Finish Button ── */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 12,
          marginBottom: 16
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: isMobile ? 24 : 28 }}>📋</span>
              <h1 style={{ ...pg, fontSize: isMobile ? 20 : 24, fontWeight: 900, margin: 0, letterSpacing: '-0.5px' }}>
                Physical Stock Audit
              </h1>
            </div>
            <p style={{ ...muted, margin: '4px 0 0', fontSize: isMobile ? 12 : 13 }}>
              Conduct manual physical count in store or godown, record discrepancies, and reconcile main inventory
            </p>
          </div>

          {/* Top Right Action Area */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
            {activeTab === 'new' ? (
              <>
                {auditedItems.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Clear all entered items for this audit session?')) setAuditedItems([]);
                    }}
                    style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, color: darkMode ? '#94A3B8' : '#64748B', fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 38 }}
                  >
                    🗑️ Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowConfirmFinishModal(true)}
                  disabled={auditedItems.length === 0}
                  style={{
                    padding: isMobile ? '10px 18px' : '10px 22px',
                    background: auditedItems.length === 0 ? (darkMode ? '#334155' : '#CBD5E1') : acc,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 9,
                    fontWeight: 800,
                    fontSize: isMobile ? 13 : 14,
                    cursor: auditedItems.length === 0 ? 'not-allowed' : 'pointer',
                    boxShadow: auditedItems.length > 0 ? `0 4px 14px ${acc}55` : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minHeight: 40,
                  }}
                >
                  <span>✓</span> Finish Audit
                  {auditedItems.length > 0 && (
                    <span style={{ background: 'rgba(255,255,255,0.25)', padding: '2px 7px', borderRadius: 10, fontSize: 11 }}>
                      {auditedItems.length}
                    </span>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => { setViewAuditReport(null); setActiveTab('new'); }}
                style={{ padding: '9px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minHeight: 40 }}
              >
                <span>➕</span> Start New Audit
              </button>
            )}
          </div>
        </div>

        {/* ── Main Tab Navigation Bar ── */}
        <div style={{ display: 'flex', gap: 8, borderBottom: `1px solid ${border}`, paddingBottom: 10, marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <button
            type="button"
            onClick={() => setActiveTab('new')}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: `1px solid ${activeTab === 'new' ? acc : 'transparent'}`,
              background: activeTab === 'new' ? `${acc}15` : 'transparent',
              color: activeTab === 'new' ? acc : (darkMode ? '#94A3B8' : '#64748B'),
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              minHeight: 38,
            }}
          >
            <span>📝 Active Audit Session</span>
            {auditedItems.length > 0 && (
              <span style={{ background: acc, color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 800 }}>
                {auditedItems.length}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => { setViewAuditReport(null); setActiveTab('history'); }}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: `1px solid ${activeTab === 'history' ? acc : 'transparent'}`,
              background: activeTab === 'history' ? `${acc}15` : 'transparent',
              color: activeTab === 'history' ? acc : (darkMode ? '#94A3B8' : '#64748B'),
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              minHeight: 38,
            }}
          >
            <span>📊 Past Audits & Reports</span>
            {pastAudits.length > 0 && (
              <span style={{ background: darkMode ? '#334155' : '#E2E8F0', color: pg.color, borderRadius: 10, padding: '1px 6px', fontSize: 10 }}>
                {pastAudits.length}
              </span>
            )}
          </button>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            TAB 1: NEW / ACTIVE STOCK AUDIT SESSION
        ══════════════════════════════════════════════════════════════ */}
        {activeTab === 'new' && (
          <div>
            {/* Top Auditor & Location Context Banner */}
            <div style={{
              background: cardBg,
              borderRadius: 12,
              border: `1px solid ${border}`,
              padding: isMobile ? '12px 14px' : '14px 18px',
              marginBottom: 16,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 12,
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${acc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: acc, fontWeight: 800, flexShrink: 0 }}>
                  👤
                </div>
                <div>
                  <div style={{ fontSize: 10, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Conducted By</div>
                  <div style={{ ...pg, fontWeight: 800, fontSize: 13 }}>
                    {auditorDisplayName} <span style={{ fontSize: 10, fontWeight: 600, color: acc, background: `${acc}15`, padding: '1px 6px', borderRadius: 4, textTransform: 'capitalize' }}>{loginRole || 'Staff'}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'center' }}>
                <div>
                  <label style={lbl}>Audit Location</label>
                  <select
                    style={{ ...inp, fontWeight: 600 }}
                    value={auditLocation}
                    onChange={e => setAuditLocation(e.target.value)}
                  >
                    {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                    {warehouses.map(w => (
                      <option key={w.id} value={`Warehouse: ${w.name}`}>Warehouse: {w.name} {w.code ? `(${w.code})` : ''}</option>
                    ))}
                  </select>
                </div>

                {warehouses.length > 0 && (
                  <div>
                    <label style={lbl}>Link Warehouse</label>
                    <select
                      style={inp}
                      value={selectedWarehouseId}
                      onChange={e => setSelectedWarehouseId(e.target.value)}
                    >
                      <option value="">None (General Store)</option>
                      {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label style={lbl}>Audit Title / Notes</label>
                  <input
                    style={inp}
                    value={auditTitle}
                    onChange={e => setAuditTitle(e.target.value)}
                    placeholder="e.g. Weekly Godown Physical Count"
                  />
                </div>
              </div>
            </div>

            {/* Product Search & Count Entry Workspace Card */}
            <div style={{
              background: cardBg,
              borderRadius: 14,
              border: `1px solid ${border}`,
              padding: isMobile ? '14px 14px' : '18px 20px',
              marginBottom: 16,
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: pg.color, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>📦</span> Step 1: Select Product & Record Physical Stock
                </span>
                <span style={{ fontSize: 11, ...muted, fontWeight: 500 }}>
                  Sorted: Positive stock 🟢 → Negative 🔴 → Zero ⚪
                </span>
              </div>

              {/* Search Row with "+ Add Product" button */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'minmax(280px, 1fr) auto',
                gap: 10,
                alignItems: 'flex-start',
                marginBottom: 14
              }}>
                <div ref={dropdownRef} style={{ position: 'relative' }}>
                  <label style={lbl}>Search Product (Name, SKU, Barcode) <span style={{ color: acc }}>*</span></label>
                  <div style={{ position: 'relative' }}>
                    <input
                      ref={prodInputRef}
                      style={{ ...inp, paddingLeft: 34, fontSize: isMobile ? 14 : 14, fontWeight: 600 }}
                      value={productQuery}
                      onChange={e => {
                        setProductQuery(e.target.value);
                        if (selectedProduct && e.target.value !== selectedProduct.name) {
                          setSelectedProduct(null);
                        }
                      }}
                      onFocus={handleFocusProductSearch}
                      onClick={handleFocusProductSearch}
                      placeholder="Search or tap to view products sorted by stock…"
                    />
                    <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 14 }}>
                      🔍
                    </span>
                    {productQuery && (
                      <button
                        type="button"
                        onClick={() => { setProductQuery(''); setSelectedProduct(null); handleFocusProductSearch(); }}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: darkMode ? '#64748B' : '#94A3B8', cursor: 'pointer', fontSize: 16, padding: '4px 6px' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Dropdown Results (Optimized for Mobile PWA & Desktop) */}
                  {showProductDD && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '100%',
                      zIndex: 100,
                      background: darkMode ? '#1E293B' : '#FFFFFF',
                      border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`,
                      borderRadius: 10,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
                      maxHeight: isMobile ? '45vh' : 320,
                      overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      marginTop: 4,
                    }}>
                      {/* Dropdown Quick Filter Chips for faster navigation */}
                      <div style={{
                        padding: '8px 10px',
                        background: darkMode ? '#0F172A' : '#F1F5F9',
                        borderBottom: `1px solid ${border}`,
                        display: 'flex',
                        gap: 6,
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                      }}>
                        {[
                          { id: 'all', label: 'All Items' },
                          { id: 'positive', label: '🟢 In Stock (> 0)' },
                          { id: 'negative', label: '🔴 Negative (< 0)' },
                          { id: 'zero', label: '⚪ Zero Stock (= 0)' },
                        ].map(chip => (
                          <button
                            key={chip.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDropdownStockFilter(chip.id);
                            }}
                            style={{
                              padding: '3px 9px',
                              borderRadius: 12,
                              border: 'none',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              background: dropdownStockFilter === chip.id ? acc : (darkMode ? '#334155' : '#E2E8F0'),
                              color: dropdownStockFilter === chip.id ? '#fff' : pg.color,
                            }}
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>

                      {productResults.length === 0 ? (
                        <div style={{ padding: 18, textAlign: 'center', fontSize: 13, ...muted }}>
                          No matching products found
                        </div>
                      ) : (
                        productResults.map(p => {
                          const stock = Number(p.current_stock ?? 0);
                          const isPositive = stock > 0;
                          const isNegative = stock < 0;

                          return (
                            <div
                              key={p.id}
                              onClick={() => handleSelectProduct(p)}
                              style={{
                                padding: isMobile ? '11px 14px' : '9px 14px',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${darkMode ? '#334155' : '#F1F5F9'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 10,
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#334155' : '#F8FAFC'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 13, color: pg.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.name}
                                </div>
                                <div style={{ fontSize: 11, ...muted, marginTop: 1 }}>
                                  SKU: {p.sku || '—'} {p.category ? `· ${p.category}` : ''} {p.unit ? `· ${p.unit}` : ''}
                                </div>
                              </div>

                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  background: isPositive ? '#10B98120' : (isNegative ? '#EF444420' : (darkMode ? '#334155' : '#E2E8F0')),
                                  color: isPositive ? '#10B981' : (isNegative ? '#EF4444' : (darkMode ? '#94A3B8' : '#64748B')),
                                }}>
                                  {isPositive ? `✓ Stock: ${stock}` : (isNegative ? `⚠️ Stock: ${stock}` : `Stock: 0`)} {p.unit || 'Pcs'}
                                </span>
                                <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>₹{fmtNum(p.selling_price)}</div>
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Add Directly prompt if item not found */}
                      <div
                        onClick={() => { setShowProductDD(false); setShowAddProdModal(true); }}
                        style={{
                          padding: '12px 14px',
                          background: `${acc}15`,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: acc,
                          fontWeight: 700,
                          fontSize: 13,
                          borderTop: `1px dashed ${acc}44`,
                        }}
                      >
                        <span>➕</span>
                        <span>Product not in list? Click to add "{productQuery || 'New Product'}" directly</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* "+ Add Product" button matching Billing pages */}
                <div style={{ alignSelf: isMobile ? 'stretch' : 'flex-end' }}>
                  {!isMobile && <label style={lbl}>&nbsp;</label>}
                  <button
                    type="button"
                    onClick={() => setShowAddProdModal(true)}
                    style={{
                      width: isMobile ? '100%' : 'auto',
                      padding: '9px 16px',
                      background: 'transparent',
                      border: `1.5px dashed ${acc}`,
                      color: acc,
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      minHeight: 40,
                    }}
                  >
                    <span>➕</span> Add Product
                  </button>
                </div>
              </div>

              {/* Selected Product & Physical Count Entry Row */}
              {selectedProduct ? (
                <form onSubmit={handleConfirmItem} style={{
                  background: darkMode ? '#0F172A' : '#F8FAFC',
                  borderRadius: 12,
                  border: `1.5px solid ${acc}44`,
                  padding: isMobile ? 14 : 16,
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 12,
                  alignItems: 'flex-end',
                }}>
                  <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                    <div style={{ fontSize: 10, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Selected Item</div>
                    <div style={{ fontSize: isMobile ? 15 : 14, fontWeight: 800, color: pg.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selectedProduct.name}
                    </div>
                    <div style={{ fontSize: 11, ...muted }}>
                      SKU: {selectedProduct.sku || '—'} · {selectedProduct.unit || 'Pcs'} · ₹{fmtNum(selectedProduct.selling_price)}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 10, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>System Stock</div>
                    <div style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: Number(selectedProduct.current_stock) > 0 ? '#10B981' : (Number(selectedProduct.current_stock) < 0 ? '#EF4444' : (darkMode ? '#94A3B8' : '#475569'))
                    }}>
                      {selectedProduct.current_stock} <span style={{ fontSize: 11, fontWeight: 500 }}>{selectedProduct.unit || 'Pcs'}</span>
                    </div>
                  </div>

                  <div>
                    <label style={{ ...lbl, color: acc }}>Physical Stock <span style={{ color: '#EF4444' }}>*</span></label>
                    <input
                      ref={physStockInputRef}
                      type="number"
                      step="any"
                      inputMode="decimal"
                      style={{ ...inp, border: `2px solid ${acc}`, fontWeight: 800, fontSize: 16 }}
                      value={itemPhysicalStock}
                      onChange={e => setItemPhysicalStock(e.target.value)}
                      placeholder="Counted qty…"
                      required
                    />
                  </div>

                  {/* Live Variance Preview */}
                  <div>
                    <div style={{ fontSize: 10, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Variance Preview</div>
                    {itemPhysicalStock !== '' && !isNaN(parseFloat(itemPhysicalStock)) ? (
                      (() => {
                        const diff = +(parseFloat(itemPhysicalStock) - parseFloat(selectedProduct.current_stock || 0)).toFixed(2);
                        if (diff === 0) {
                          return <div style={{ fontSize: 13, fontWeight: 800, color: '#10B981' }}>✓ Exact Match (0)</div>;
                        } else if (diff < 0) {
                          return <div style={{ fontSize: 13, fontWeight: 800, color: '#EF4444' }}>⚠️ Shortage ({diff})</div>;
                        } else {
                          return <div style={{ fontSize: 13, fontWeight: 800, color: '#3B82F6' }}>🟢 Surplus (+{diff})</div>;
                        }
                      })()
                    ) : (
                      <div style={{ fontSize: 12, ...muted }}>Enter count</div>
                    )}
                  </div>

                  <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                    <label style={lbl}>Remarks / Notes (optional)</label>
                    <input
                      style={inp}
                      value={itemNotes}
                      onChange={e => setItemNotes(e.target.value)}
                      placeholder="e.g. Rack 2, damaged box…"
                    />
                  </div>

                  <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                    <button
                      type="submit"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: acc,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 800,
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        minHeight: 42,
                      }}
                    >
                      <span>➕</span> Confirm & Add to Audit
                    </button>
                  </div>
                </form>
              ) : (
                <div style={{ padding: '12px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, border: `1px dashed ${border}`, fontSize: 12, ...muted, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>💡</span>
                  <span>Select any product from the dropdown above to view its current system stock and record physical stock counted in store or godown.</span>
                </div>
              )}
            </div>

            {/* Live Audit Metrics Cards Ribbon (Responsive 2 cols on mobile, 5 cols on desktop) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 10,
              marginBottom: 16
            }}>
              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Audited Items</div>
                <div style={{ fontSize: 20, fontWeight: 900, ...pg, marginTop: 2 }}>{activeMetrics.totalItems}</div>
                <div style={{ fontSize: 10, ...muted }}>Recorded in session</div>
              </div>

              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Matched Stock</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#10B981', marginTop: 2 }}>{activeMetrics.matchedCount}</div>
                <div style={{ fontSize: 10, ...muted }}>No discrepancy</div>
              </div>

              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Shortages Found</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#EF4444', marginTop: 2 }}>
                  {activeMetrics.shortageCount} <span style={{ fontSize: 11, fontWeight: 600 }}>({activeMetrics.totalShortageUnits} u)</span>
                </div>
                <div style={{ fontSize: 10, ...muted }}>Physical &lt; system</div>
              </div>

              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Surpluses Found</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#3B82F6', marginTop: 2 }}>
                  {activeMetrics.surplusCount} <span style={{ fontSize: 11, fontWeight: 600 }}>({activeMetrics.totalSurplusUnits} u)</span>
                </div>
                <div style={{ fontSize: 10, ...muted }}>Physical &gt; system</div>
              </div>

              <div style={{
                background: cardBg,
                borderRadius: 10,
                border: `1px solid ${border}`,
                padding: '10px 12px',
                gridColumn: isMobile ? '1 / -1' : 'auto'
              }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Net Discrepancy Value</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: activeMetrics.netVarianceValue < 0 ? '#EF4444' : (activeMetrics.netVarianceValue > 0 ? '#10B981' : pg.color), marginTop: 2 }}>
                  ₹{fmtNum(activeMetrics.netVarianceValue)}
                </div>
                <div style={{ fontSize: 10, ...muted }}>Estimated financial variance</div>
              </div>
            </div>

            {/* List of Audited Items: Mobile Card Layout vs Desktop Table */}
            <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, ...pg }}>
                    📋 Audited Items List ({auditedItems.length})
                  </h3>
                  <div style={{ fontSize: 11, ...muted }}>Review all physical stock entries before clicking Finish Audit</div>
                </div>

                {auditedItems.length > 0 && !isMobile && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmFinishModal(true)}
                    style={{ padding: '8px 16px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <span>✓</span> Finish Audit
                  </button>
                )}
              </div>

              {auditedItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: isMobile ? '36px 16px' : '48px 20px', ...muted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: pg.color, marginBottom: 4 }}>No stock items added yet</div>
                  <div style={{ fontSize: 12 }}>Use the search box above to find or add products and confirm counted quantities.</div>
                </div>
              ) : isMobile ? (
                /* ── PWA Mobile Card Layout ── */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10 }}>
                  {auditedItems.map((item, idx) => {
                    const isMatch = item.variance === 0;
                    const isShortage = item.variance < 0;
                    return (
                      <div key={item.key} style={{
                        background: darkMode ? '#0F172A' : '#F8FAFC',
                        borderRadius: 10,
                        border: `1px solid ${border}`,
                        padding: 12,
                        position: 'relative',
                      }}>
                        {/* Top: #, Title, Location, Delete */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, ...muted, fontWeight: 700 }}>#{idx + 1}</div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: pg.color, wordBreak: 'break-word' }}>
                              {item.product_name}
                            </div>
                            <div style={{ fontSize: 11, ...muted }}>
                              SKU: {item.sku || '—'} · {item.category || 'General'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <span style={{ fontSize: 10, background: darkMode ? '#334155' : '#E2E8F0', padding: '2px 6px', borderRadius: 4, ...pg }}>
                              {item.location}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(item.key)}
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, padding: '2px 4px' }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        {/* Middle: 3-column stats with inline physical stock edit */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1.2fr',
                          gap: 6,
                          alignItems: 'center',
                          background: cardBg,
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: `1px solid ${border}`,
                          marginTop: 6
                        }}>
                          <div>
                            <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>System</div>
                            <div style={{ fontSize: 13, fontWeight: 700, ...pg }}>
                              {fmtNum(item.system_stock)} {item.unit}
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Physical</div>
                            <input
                              type="number"
                              step="any"
                              inputMode="decimal"
                              value={item.physical_stock}
                              onChange={e => handleUpdateItemQty(item.key, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '4px 6px',
                                borderRadius: 6,
                                border: `1.5px solid ${acc}`,
                                background: darkMode ? '#0F172A' : '#fff',
                                color: pg.color,
                                fontWeight: 800,
                                fontSize: 13,
                                textAlign: 'right',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>

                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Variance</div>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: 8,
                              fontSize: 10,
                              fontWeight: 800,
                              background: isMatch ? '#10B98120' : (isShortage ? '#EF444420' : '#3B82F620'),
                              color: isMatch ? '#10B981' : (isShortage ? '#EF4444' : '#3B82F6'),
                            }}>
                              {isMatch ? '✓ Match (0)' : (isShortage ? `${item.variance} Short` : `+${item.variance} Surp`)}
                            </span>
                          </div>
                        </div>

                        {item.notes && (
                          <div style={{ fontSize: 11, ...muted, marginTop: 6, fontStyle: 'italic' }}>
                            Note: {item.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ── Desktop Data Table Layout ── */
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', width: 40 }}>#</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Product Details</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Location</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>System Stock</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>Physical Count</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center' }}>Variance (Diff)</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>Discrepancy (₹)</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Remarks</th>
                        <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center', width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditedItems.map((item, idx) => {
                        const isMatch = item.variance === 0;
                        const isShortage = item.variance < 0;
                        return (
                          <tr key={item.key} style={{ borderBottom: `1px solid ${border}` }}>
                            <td style={{ padding: '10px 12px', ...muted, fontSize: 11 }}>{idx + 1}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ fontWeight: 700, color: pg.color }}>{item.product_name}</div>
                              <div style={{ fontSize: 11, ...muted }}>
                                SKU: {item.sku || '—'} {item.category ? `· ${item.category}` : ''}
                              </div>
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ fontSize: 11, background: darkMode ? '#334155' : '#E2E8F0', padding: '2px 8px', borderRadius: 4, ...pg }}>
                                {item.location}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, ...muted }}>
                              {fmtNum(item.system_stock)} {item.unit}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <input
                                type="number"
                                step="any"
                                inputMode="decimal"
                                value={item.physical_stock}
                                onChange={e => handleUpdateItemQty(item.key, e.target.value)}
                                style={{
                                  width: 80,
                                  textAlign: 'right',
                                  padding: '4px 6px',
                                  borderRadius: 6,
                                  border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`,
                                  background: darkMode ? '#0F172A' : '#fff',
                                  color: pg.color,
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              />
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '3px 10px',
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 800,
                                background: isMatch ? '#10B98120' : (isShortage ? '#EF444420' : '#3B82F620'),
                                color: isMatch ? '#10B981' : (isShortage ? '#EF4444' : '#3B82F6'),
                              }}>
                                {isMatch ? '✓ Match (0)' : (isShortage ? `${item.variance} Shortage` : `+${item.variance} Surplus`)}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: item.variance_value < 0 ? '#EF4444' : (item.variance_value > 0 ? '#10B981' : muted.color) }}>
                              {item.variance_value !== 0 ? `₹${fmtNum(item.variance_value)}` : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 12, ...muted }}>
                              {item.notes || '—'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.key)}
                                title="Remove item"
                                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, padding: '2px 6px' }}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Floating / Sticky Mobile Bottom Action Bar for PWA */}
            {isMobile && auditedItems.length > 0 && (
              <div style={{
                position: 'fixed',
                bottom: 12,
                left: 12,
                right: 12,
                zIndex: 80,
                background: darkMode ? '#1E293B' : '#FFFFFF',
                borderRadius: 14,
                border: `1.5px solid ${acc}`,
                padding: '10px 16px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, ...pg }}>{auditedItems.length} Products Audited</div>
                  <div style={{ fontSize: 10, ...muted }}>Tap to finish & save report</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowConfirmFinishModal(true)}
                  style={{
                    padding: '9px 18px',
                    background: acc,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 9,
                    fontWeight: 800,
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minHeight: 40,
                  }}
                >
                  <span>✓</span> Finish Audit
                </button>
              </div>
            )}

            {/* Bottom Finalize Bar on Desktop */}
            {!isMobile && auditedItems.length > 0 && (
              <div style={{ background: cardBg, borderRadius: 12, border: `1px solid ${border}`, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, ...pg }}>
                    Audit Ready to Finish ({auditedItems.length} products recorded)
                  </div>
                  <div style={{ fontSize: 12, ...muted }}>
                    Once finished, this report will be stored in database with date & time, ready for manual stock reconciliation.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowConfirmFinishModal(true)}
                  style={{ padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, minHeight: 42 }}
                >
                  <span>✓</span> Finish Audit & Save Report
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB 2: PAST AUDITS & GENERATED REPORTS LIST
        ══════════════════════════════════════════════════════════════ */}
        {activeTab === 'history' && (
          <div>
            {/* KPI Stats overview for past audits */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 10,
              marginBottom: 16
            }}>
              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Total Audits</div>
                <div style={{ fontSize: 20, fontWeight: 900, ...pg }}>{pastStats.total_audits || pastAudits.length}</div>
              </div>
              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Pending Reconcile</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#F59E0B' }}>{pastStats.pending_reconcile_count || 0}</div>
              </div>
              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Reconciled</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#10B981' }}>{pastStats.reconciled_count || 0}</div>
              </div>
              <div style={{ background: cardBg, borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Total Shortages</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#EF4444' }}>{pastStats.total_shortages || 0}</div>
              </div>
              <div style={{
                background: cardBg,
                borderRadius: 10,
                border: `1px solid ${border}`,
                padding: '10px 12px',
                gridColumn: isMobile ? '1 / -1' : 'auto'
              }}>
                <div style={{ fontSize: 10, ...muted, fontWeight: 700, textTransform: 'uppercase' }}>Total Surpluses</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#3B82F6' }}>{pastStats.total_surpluses || 0}</div>
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div style={{
              background: cardBg,
              borderRadius: 12,
              border: `1px solid ${border}`,
              padding: 12,
              marginBottom: 16,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 10,
              alignItems: 'stretch'
            }}>
              <input
                style={{ ...inp, flex: 1 }}
                placeholder="Search by audit #, title, auditor name, location…"
                value={pastSearch}
                onChange={e => setPastSearch(e.target.value)}
              />
              <select
                style={{ ...inp, width: isMobile ? '100%' : 'auto', minWidth: 160 }}
                value={pastFilterStatus}
                onChange={e => setPastFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="completed">⏳ Pending Reconciliation</option>
                <option value="reconciled">✓ Reconciled</option>
                <option value="draft">📝 Draft</option>
              </select>
              <button
                type="button"
                onClick={loadPastAudits}
                style={{ padding: '9px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', minHeight: 40 }}
              >
                🔍 Search
              </button>
            </div>

            {/* Past Audits List Table */}
            <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, overflow: 'hidden' }}>
              {loadingPastAudits ? (
                <div style={{ padding: 40, textAlign: 'center', ...muted }}>Loading audit history…</div>
              ) : pastAudits.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', ...muted }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📜</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: pg.color, marginBottom: 4 }}>No stock audits recorded yet</div>
                  <div style={{ fontSize: 13, marginBottom: 14 }}>Perform your first physical stock audit by clicking the button below.</div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('new')}
                    style={{ padding: '10px 20px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >
                    ➕ Start Stock Audit
                  </button>
                </div>
              ) : isMobile ? (
                /* Mobile Card View for Past Audits */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10 }}>
                  {pastAudits.map(audit => {
                    const isReconciled = audit.status === 'reconciled';
                    return (
                      <div key={audit.id} style={{
                        background: darkMode ? '#0F172A' : '#F8FAFC',
                        borderRadius: 12,
                        border: `1px solid ${border}`,
                        padding: 14,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                          <div>
                            <span style={{ fontWeight: 800, fontSize: 14, color: acc }}>{audit.audit_number}</span>
                            <div style={{ fontWeight: 700, fontSize: 13, ...pg, marginTop: 2 }}>{audit.title || 'Stock Audit'}</div>
                            <div style={{ fontSize: 11, ...muted }}>
                              {audit.location} · {formatDateTime(audit.audit_date || audit.created_at)}
                            </div>
                          </div>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 800,
                            background: isReconciled ? '#10B98120' : '#F59E0B20',
                            color: isReconciled ? '#10B981' : '#F59E0B',
                          }}>
                            {isReconciled ? '✓ Reconciled' : '⏳ Pending'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', gap: 6, margin: '8px 0', fontSize: 11 }}>
                          <span style={{ padding: '2px 6px', background: darkMode ? '#334155' : '#E2E8F0', borderRadius: 4, ...pg }}>
                            📦 {audit.total_items} items
                          </span>
                          <span style={{ padding: '2px 6px', background: '#10B98120', color: '#10B981', borderRadius: 4, fontWeight: 700 }}>
                            ✓ {audit.matched_items_count}
                          </span>
                          {audit.shortage_items_count > 0 && (
                            <span style={{ padding: '2px 6px', background: '#EF444420', color: '#EF4444', borderRadius: 4, fontWeight: 700 }}>
                              - {audit.shortage_items_count} short
                            </span>
                          )}
                          {audit.surplus_items_count > 0 && (
                            <span style={{ padding: '2px 6px', background: '#3B82F620', color: '#3B82F6', borderRadius: 4, fontWeight: 700 }}>
                              + {audit.surplus_items_count} surp
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${border}` }}>
                          <button
                            type="button"
                            onClick={() => handleOpenAuditDetails(audit.id)}
                            style={{ flex: 1, padding: '7px 10px', background: `${acc}15`, border: `1px solid ${acc}44`, borderRadius: 6, color: acc, fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}
                          >
                            👁️ View Report
                          </button>
                          {!isReconciled && (
                            <button
                              type="button"
                              onClick={() => handleReconcile(audit.id)}
                              style={{ flex: 1, padding: '7px 10px', background: '#10B981', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}
                            >
                              ⚡ Reconcile
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDeleteAudit(audit.id)}
                            style={{ padding: '7px 10px', background: 'none', border: `1px solid ${border}`, borderRadius: 6, color: '#EF4444', cursor: 'pointer', fontSize: 14 }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Desktop Table View for Past Audits */
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Audit #</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Date & Time</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Title & Location</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Auditor</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center' }}>Items</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center' }}>Discrepancies</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center' }}>Status</th>
                        <th style={{ padding: '11px 14px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastAudits.map(audit => {
                        const isReconciled = audit.status === 'reconciled';
                        return (
                          <tr key={audit.id} style={{ borderBottom: `1px solid ${border}` }}>
                            <td style={{ padding: '11px 14px', fontWeight: 800, color: acc }}>
                              {audit.audit_number}
                            </td>
                            <td style={{ padding: '11px 14px', ...pg }}>
                              {formatDateTime(audit.audit_date || audit.created_at)}
                            </td>
                            <td style={{ padding: '11px 14px' }}>
                              <div style={{ fontWeight: 700, color: pg.color }}>{audit.title || 'Stock Audit'}</div>
                              <div style={{ fontSize: 11, ...muted }}>
                                Location: {audit.location || 'Store'} {audit.warehouse_name ? `(${audit.warehouse_name})` : ''}
                              </div>
                            </td>
                            <td style={{ padding: '11px 14px' }}>
                              <div style={{ fontWeight: 600, ...pg }}>{audit.audited_by_name || 'Staff'}</div>
                              <div style={{ fontSize: 10, ...muted, textTransform: 'capitalize' }}>{audit.audited_by_role || 'staff'}</div>
                            </td>
                            <td style={{ padding: '11px 14px', textAlign: 'center', fontWeight: 700, ...pg }}>
                              {audit.total_items}
                            </td>
                            <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                <span title="Matches" style={{ fontSize: 10, padding: '2px 6px', background: '#10B98120', color: '#10B981', borderRadius: 4, fontWeight: 700 }}>
                                  ✓ {audit.matched_items_count}
                                </span>
                                {audit.shortage_items_count > 0 && (
                                  <span title="Shortages" style={{ fontSize: 10, padding: '2px 6px', background: '#EF444420', color: '#EF4444', borderRadius: 4, fontWeight: 700 }}>
                                    - {audit.shortage_items_count}
                                  </span>
                                )}
                                {audit.surplus_items_count > 0 && (
                                  <span title="Surpluses" style={{ fontSize: 10, padding: '2px 6px', background: '#3B82F620', color: '#3B82F6', borderRadius: 4, fontWeight: 700 }}>
                                    + {audit.surplus_items_count}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                              <span style={{
                                padding: '3px 10px',
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 800,
                                background: isReconciled ? '#10B98120' : '#F59E0B20',
                                color: isReconciled ? '#10B981' : '#F59E0B',
                              }}>
                                {isReconciled ? '✓ Reconciled' : '⏳ Pending'}
                              </span>
                            </td>
                            <td style={{ padding: '11px 14px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={() => handleOpenAuditDetails(audit.id)}
                                  style={{ padding: '5px 10px', background: `${acc}15`, border: `1px solid ${acc}44`, borderRadius: 6, color: acc, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                >
                                  👁️ View Report
                                </button>
                                {!isReconciled && (
                                  <button
                                    type="button"
                                    onClick={() => handleReconcile(audit.id)}
                                    style={{ padding: '5px 10px', background: '#10B981', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    ⚡ Reconcile
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAudit(audit.id)}
                                  title="Delete Audit"
                                  style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '5px 6px', fontSize: 14 }}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            CONFIRM FINISH AUDIT MODAL
        ══════════════════════════════════════════════════════════════ */}
        {showConfirmFinishModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}>
            <div style={{
              background: cardBg,
              borderRadius: isMobile ? '16px 16px 0 0' : 14,
              width: '100%',
              maxWidth: isMobile ? '100%' : 500,
              maxHeight: isMobile ? '92dvh' : '90vh',
              border: `1px solid ${border}`,
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
              overflowY: 'auto'
            }}>
              <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)` }} />
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, ...pg }}>✓ Finish Stock Audit</h3>
                <button type="button" onClick={() => setShowConfirmFinishModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>×</button>
              </div>

              <div style={{ padding: isMobile ? 16 : 22 }}>
                <p style={{ margin: '0 0 14px', fontSize: 13, ...pg }}>
                  You are about to save the physical stock verification report to the database. You can manually reconcile the recorded stock into main inventory at any time.
                </p>

                <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, padding: 12, marginBottom: 14, border: `1px solid ${border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, fontSize: 12 }}>
                    <div><span style={muted}>Auditor:</span> <strong>{auditorDisplayName}</strong></div>
                    <div><span style={muted}>Location:</span> <strong>{auditLocation}</strong></div>
                    <div><span style={muted}>Total Items:</span> <strong>{activeMetrics.totalItems} products</strong></div>
                    <div><span style={muted}>Matched Items:</span> <strong style={{ color: '#10B981' }}>{activeMetrics.matchedCount}</strong></div>
                    <div><span style={muted}>Shortages:</span> <strong style={{ color: '#EF4444' }}>{activeMetrics.shortageCount} ({activeMetrics.totalShortageUnits} units)</strong></div>
                    <div><span style={muted}>Surpluses:</span> <strong style={{ color: '#3B82F6' }}>{activeMetrics.surplusCount} ({activeMetrics.totalSurplusUnits} units)</strong></div>
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>Audit Title / Batch Name</label>
                  <input
                    style={inp}
                    value={auditTitle}
                    onChange={e => setAuditTitle(e.target.value)}
                    placeholder="e.g. Store Physical Stock Audit"
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Final Audit Notes / Remarks (Optional)</label>
                  <textarea
                    style={{ ...inp, minHeight: 64, resize: 'vertical' }}
                    value={auditNotes}
                    onChange={e => setAuditNotes(e.target.value)}
                    placeholder="Any summary observations, godown condition, damaged packaging, etc."
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setShowConfirmFinishModal(false)}
                    style={{ padding: '9px 16px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, color: darkMode ? '#94A3B8' : '#64748B', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 42 }}
                  >
                    Continue Auditing
                  </button>
                  <button
                    type="button"
                    disabled={finishing}
                    onClick={handleSaveAudit}
                    style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: finishing ? 'not-allowed' : 'pointer', minHeight: 42 }}
                  >
                    {finishing ? 'Saving Report…' : '✓ Confirm & Save Report'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            VIEW SINGLE AUDIT REPORT & MANUAL RECONCILIATION MODAL
        ══════════════════════════════════════════════════════════════ */}
        {viewAuditReport && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16, overflowY: 'auto' }}>
            <div style={{
              background: cardBg,
              borderRadius: isMobile ? '16px 16px 0 0' : 16,
              width: '100%',
              maxWidth: isMobile ? '100%' : 960,
              maxHeight: isMobile ? '92dvh' : '92vh',
              border: `1px solid ${border}`,
              boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              
              {/* Report Header */}
              <div style={{ padding: isMobile ? '14px 16px' : '18px 24px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 20 }}>📋</span>
                    <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 900, ...pg }}>
                      {viewAuditReport.audit_number}
                    </h2>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 800,
                      background: viewAuditReport.status === 'reconciled' ? '#10B98120' : '#F59E0B20',
                      color: viewAuditReport.status === 'reconciled' ? '#10B981' : '#F59E0B',
                    }}>
                      {viewAuditReport.status === 'reconciled' ? '✓ Reconciled' : '⏳ Pending Reconcile'}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, ...muted, marginTop: 4 }}>
                    By <strong>{viewAuditReport.audited_by_name}</strong> on {formatDateTime(viewAuditReport.audit_date || viewAuditReport.created_at)} · Location: <strong>{viewAuditReport.location}</strong>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => window.print()}
                    style={{ padding: '6px 12px', background: darkMode ? '#334155' : '#E2E8F0', border: 'none', borderRadius: 8, color: pg.color, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    🖨️ Print
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewAuditReport(null)}
                    style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B', lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Scrollable Report Content */}
              <div style={{ padding: isMobile ? 14 : 24, overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
                {reconcileSuccessMsg && (
                  <div style={{ marginBottom: 14, padding: '10px 14px', background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 8, color: '#065F46', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>✓</span> {reconcileSuccessMsg}
                  </div>
                )}

                {/* Reconciliation Action Hero Banner if Pending */}
                {viewAuditReport.status !== 'reconciled' ? (
                  <div style={{
                    background: darkMode ? '#0F172A' : '#EFF6FF',
                    border: `1.5px solid ${acc}44`,
                    borderRadius: 12,
                    padding: isMobile ? 14 : 18,
                    marginBottom: 16,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: 12
                  }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: acc, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>⚡</span> Ready for Manual Stock Reconciliation
                      </div>
                      <div style={{ fontSize: 12, ...muted, marginTop: 2 }}>
                        Clicking the button will update <strong>current stock</strong> in your main product inventory to match the counted physical stock for all items, and log audit movement entries.
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={reconciling}
                      onClick={() => handleReconcile(viewAuditReport.id)}
                      style={{
                        padding: '10px 20px',
                        background: '#10B981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 800,
                        fontSize: 13,
                        cursor: reconciling ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                        minHeight: 42
                      }}
                    >
                      <span>⚡</span> {reconciling ? 'Updating Inventory…' : 'Reconcile Stock to Main Inventory'}
                    </button>
                  </div>
                ) : (
                  <div style={{ background: '#D1FAE525', border: '1px solid #10B98155', borderRadius: 12, padding: 12, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>✓</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#10B981' }}>
                        Inventory Reconciled Successfully
                      </div>
                      <div style={{ fontSize: 11, ...muted }}>
                        Physical counted quantities were reconciled into main stock on {formatDateTime(viewAuditReport.reconciled_at)} by {viewAuditReport.reconciled_by_name || 'Authorized User'}.
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary Metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 16 }}>
                  <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', padding: 10, borderRadius: 8, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Total Items</div>
                    <div style={{ fontSize: 16, fontWeight: 800, ...pg }}>{viewAuditReport.total_items}</div>
                  </div>
                  <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', padding: 10, borderRadius: 8, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Matched Items</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#10B981' }}>{viewAuditReport.matched_items_count}</div>
                  </div>
                  <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', padding: 10, borderRadius: 8, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Shortages</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#EF4444' }}>{viewAuditReport.shortage_items_count}</div>
                  </div>
                  <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', padding: 10, borderRadius: 8, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 9, ...muted, textTransform: 'uppercase', fontWeight: 700 }}>Surpluses</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#3B82F6' }}>{viewAuditReport.surplus_items_count}</div>
                  </div>
                </div>

                {viewAuditReport.notes && (
                  <div style={{ marginBottom: 16, padding: 10, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 10, ...muted, fontWeight: 700 }}>Audit Notes:</div>
                    <div style={{ fontSize: 12, ...pg, marginTop: 2 }}>{viewAuditReport.notes}</div>
                  </div>
                )}

                {/* Items Detail Table / Mobile Card View */}
                {isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {viewAuditItems.map((item, idx) => {
                      const isMatch = item.variance === 0;
                      const isShortage = item.variance < 0;
                      return (
                        <div key={item.id} style={{
                          background: darkMode ? '#0F172A' : '#F8FAFC',
                          borderRadius: 8,
                          border: `1px solid ${border}`,
                          padding: 10
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                            <div>
                              <div style={{ fontSize: 10, ...muted }}>#{idx + 1}</div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: pg.color }}>{item.product_name}</div>
                              <div style={{ fontSize: 10, ...muted }}>SKU: {item.sku || '—'} · {item.unit}</div>
                            </div>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: 8,
                              fontSize: 10,
                              fontWeight: 800,
                              background: isMatch ? '#10B98120' : (isShortage ? '#EF444420' : '#3B82F620'),
                              color: isMatch ? '#10B981' : (isShortage ? '#EF4444' : '#3B82F6'),
                            }}>
                              {isMatch ? 'Match (0)' : (isShortage ? `${item.variance} Short` : `+${item.variance} Surp`)}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, background: cardBg, padding: '6px 8px', borderRadius: 6, fontSize: 11, marginTop: 4 }}>
                            <div>
                              <span style={{ fontSize: 9, ...muted }}>System: </span>
                              <strong>{fmtNum(item.system_stock)}</strong>
                            </div>
                            <div>
                              <span style={{ fontSize: 9, ...muted }}>Counted: </span>
                              <strong>{fmtNum(item.physical_stock)}</strong>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: 9, ...muted }}>Reconciled: </span>
                              <strong style={{ color: item.is_reconciled ? '#10B981' : '#F59E0B' }}>
                                {item.is_reconciled ? '✓ Yes' : 'Pending'}
                              </strong>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${border}` }}>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Product</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>System Qty</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>Counted Qty</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center' }}>Variance</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'right' }}>Est. Discrepancy</th>
                          <th style={{ padding: '10px 12px', fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B', textAlign: 'center' }}>Reconciled</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewAuditItems.map(item => {
                          const isMatch = item.variance === 0;
                          const isShortage = item.variance < 0;
                          return (
                            <tr key={item.id} style={{ borderBottom: `1px solid ${border}` }}>
                              <td style={{ padding: '9px 12px' }}>
                                <div style={{ fontWeight: 700, color: pg.color }}>{item.product_name}</div>
                                <div style={{ fontSize: 10, ...muted }}>SKU: {item.sku || '—'} · {item.unit}</div>
                              </td>
                              <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, ...muted }}>
                                {fmtNum(item.system_stock)}
                              </td>
                              <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, ...pg }}>
                                {fmtNum(item.physical_stock)}
                              </td>
                              <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  background: isMatch ? '#10B98120' : (isShortage ? '#EF444420' : '#3B82F620'),
                                  color: isMatch ? '#10B981' : (isShortage ? '#EF4444' : '#3B82F6'),
                                }}>
                                  {isMatch ? '✓ Match (0)' : (isShortage ? `${item.variance}` : `+${item.variance}`)}
                                </span>
                              </td>
                              <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: item.variance_value < 0 ? '#EF4444' : (item.variance_value > 0 ? '#10B981' : muted.color) }}>
                                {item.variance_value !== 0 ? `₹${fmtNum(item.variance_value)}` : '—'}
                              </td>
                              <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                {item.is_reconciled ? (
                                  <span style={{ color: '#10B981', fontWeight: 700, fontSize: 11 }}>✓ Yes</span>
                                ) : (
                                  <span style={{ color: '#F59E0B', fontWeight: 600, fontSize: 11 }}>Pending</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{ padding: isMobile ? '12px 16px' : '14px 24px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setViewAuditReport(null)}
                  style={{ padding: '8px 18px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, color: pg.color, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 38 }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Full Detailed Product Modal (Exact same as used in Products & across the entire application) */}
        {showAddProdModal && (
          <ProductModal
            product={null}
            initialData={productQuery && productQuery.trim() ? { name: productQuery.trim() } : null}
            onClose={() => setShowAddProdModal(false)}
            onSuccess={handleProductCreated}
          />
        )}
      </div>
    </Layout>
  );
};

export default StockAudit;
